import { useEffect, useMemo, useState } from 'react';
import { ReuseSettings, SourceBanner, StartFrom } from '@/components/reuse';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, BookOpen, Eye, EyeOff, Plus, Trash2 } from 'lucide-react';
import {
  AUDIENCE_BADGES,
  type Audience,
  type AudienceScope,
  COURSE_LEVELS,
  type Block,
  type CourseInput,
  type CourseOutline,
  type LessonInput,
} from '@ready/shared';
import { AudiencePicker } from '@/components/audience-picker';
import {
  useApi,
  type ContentOwner,
  type Course,
  type CourseDraft,
  type DraftLesson,
} from '@/lib/api';
import { SearchBox, TopicPicker } from '@/components/catalog';
import { useDebounced } from '@/lib/use-debounced';
import { formatBytes } from '@/lib/files';
import {
  Badge,
  Button,
  Card,
  ErrorText,
  Field,
  Input,
  PageHeader,
  Select,
  Textarea,
} from '@/components/ui';
import { CopyLinkButton, PostersButton, ListCount } from '@/components/quiz/authoring';
import { AiButton, useAiEnabled } from '@/components/ai/ai-assist';
import { OutlineAssist } from '@/components/ai/outline-assist';
import { LessonAssist } from '@/components/ai/lesson-assist';
import { LessonPreview } from '@/components/course/lesson-preview';
import {
  BLOCK_TYPES,
  BlockCard,
  isPending,
  newBlockId,
  type DraftBlock,
} from '@/components/course/block-editor';
import {
  FilePicker,
  STATUS_LABEL,
  UploadProgress,
  useCourseFiles,
} from '@/components/course/file-picker';
import { useUpload } from '@/components/course/upload';

/*
 * Course authoring: the list of an owner's courses, a short "new course"
 * page, the course editor (content outline, details, files) and the lesson
 * editor (blocks). Works for org courses and Nirvik's library alike.
 */

const ownerKey = (o: ContentOwner) => (o.kind === 'org' ? o.orgId : 'library');
const ownerOf = (course: Course): ContentOwner =>
  course.ownerOrgId ? { kind: 'org', orgId: course.ownerOrgId } : { kind: 'library' };
const listPath = (course: Course) =>
  course.ownerOrgId
    ? `/organizations/${course.ownerOrgId}/library/courses`
    : '/admin/library/courses';

const STATUS: Record<Course['status'], string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
};

export function CourseList({ owner, base }: { owner: ContentOwner; base: string }) {
  const api = useApi();
  const [text, setText] = useState('');
  const q = useDebounced(text.trim());
  const courses = useQuery({
    queryKey: ['courses', ownerKey(owner), q],
    queryFn: () => api.courses(owner, q),
    placeholderData: (prev) => prev,
  });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Self-paced courses with lessons, files and an optional final quiz.
        </p>
        <Link to={`${base}/courses/new`}>
          <Button>
            <Plus className="size-4" aria-hidden /> New course
          </Button>
        </Link>
      </div>
      <SearchBox value={text} onChange={setText} placeholder="Search courses by title" />
      <ErrorText error={courses.error} />
      {courses.data && <ListCount shown={courses.data.courses.length} total={courses.data.total} />}
      {courses.data?.courses.length === 0 && (
        <Card>
          <p className="text-sm text-muted-foreground">
            {q
              ? 'No courses match that title.'
              : 'No courses yet. Create one, then add modules and lessons.'}
          </p>
        </Card>
      )}
      {courses.data?.courses.map((c) => (
        <Card key={c.id} className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <Link to={`/courses/${c.id}/edit`} className="font-medium hover:text-primary">
              {c.title}
            </Link>
            <div className="mt-1 flex flex-wrap gap-2">
              <Badge>{STATUS[c.status]}</Badge>
              {c.status === 'published' && c.draftChangedAt && <Badge>Unpublished changes</Badge>}
              <Badge>
                {c.lessonCount} lesson{c.lessonCount === 1 ? '' : 's'}
              </Badge>
              <Badge>{AUDIENCE_BADGES[c.audience]}</Badge>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {c.status === 'published' && (
              <>
                <CopyLinkButton path={`/c/${c.slug}`} />
                <PostersButton path={`/c/${c.slug}`} />
              </>
            )}
            <Link to={`/courses/${c.id}/edit`}>
              <Button variant="ghost">Edit</Button>
            </Link>
          </div>
        </Card>
      ))}
    </div>
  );
}

export function NewCourseForm({
  owner,
  back,
}: {
  owner: ContentOwner;
  back: { to: string; label: string };
}) {
  const api = useApi();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [audience, setAudience] = useState<Audience>(owner.kind === 'library' ? 'public' : 'org');
  const [scope, setScope] = useState<AudienceScope | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api.createCourse(owner, {
        title,
        summary: summary || undefined,
        audience,
        audienceScope: scope,
      }),
    onSuccess: (course) => navigate(`/courses/${course.id}/edit`),
  });
  return (
    <div className="max-w-3xl">
      <PageHeader
        title="New course"
        description="Name it and say who it's for. You'll add modules and lessons next."
        back={back}
      />
      <Card>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Field label="Title" htmlFor="course-title">
            <Input
              id="course-title"
              required
              minLength={3}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Agentic AI foundations"
            />
          </Field>
          <Field label="One-line summary (optional)" htmlFor="course-summary">
            <Input
              id="course-summary"
              maxLength={500}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Build and deploy your first AI agent"
            />
          </Field>
          <AudiencePicker
            owner={owner}
            id="course-audience"
            label="Who can take it"
            audience={audience}
            scope={scope}
            onChange={(a, s) => {
              setAudience(a as Audience);
              setScope(s);
            }}
          />
          <Button type="submit" disabled={create.isPending}>
            Create and add lessons
          </Button>
          <ErrorText error={create.error} />
        </form>
      </Card>
      <div className="mt-6">
        <StartFrom kind="course" owner={owner} />
      </div>
    </div>
  );
}

// ─── Course editor ─────────────────────────────────────────────────────

export function CourseEditorPage() {
  const { courseId = '' } = useParams();
  const api = useApi();
  const draft = useQuery({
    queryKey: ['courseDraft', courseId],
    queryFn: () => api.courseDraft(courseId),
  });
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'content';
  if (draft.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (draft.isError) return <ErrorText error={draft.error} />;
  const { course } = draft.data;
  const tabs = [
    { key: 'content', label: 'Content' },
    { key: 'details', label: 'Details' },
    { key: 'files', label: 'Files' },
  ];
  return (
    <div className="space-y-6">
      <CourseHeader draft={draft.data} />
      <SourceBanner
        kind="course"
        source={draft.data.source}
        customized={Boolean(draft.data.overlay)}
      />
      <nav className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden">
        <div className="flex w-max min-w-full gap-1 border-b border-border">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setParams({ tab: t.key }, { replace: true })}
              aria-current={tab === t.key ? 'page' : undefined}
              className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2.5 text-sm ${tab === t.key ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </nav>
      {tab === 'details' ? (
        <CourseDetails course={course} />
      ) : tab === 'files' ? (
        <CourseFiles course={course} />
      ) : (
        <OutlineEditor draft={draft.data} />
      )}
    </div>
  );
}

function CourseHeader({ draft }: { draft: CourseDraft }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { course, latestVersion } = draft;
  const publish = useMutation({
    mutationFn: () => api.publishCourse(course.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['courseDraft', course.id] }),
  });
  const archive = useMutation({
    mutationFn: (archived: boolean) => api.archiveCourse(course.id, archived),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['courseDraft', course.id] }),
  });
  const changed = !latestVersion || Boolean(course.draftChangedAt);
  return (
    <div>
      <Link
        to={listPath(course)}
        className="-my-2 inline-block py-2 text-sm text-muted-foreground hover:text-foreground"
      >
        ← Courses
      </Link>
      <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold">{course.title}</h1>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge>{STATUS[course.status]}</Badge>
            {latestVersion && <Badge>Version {latestVersion.version}</Badge>}
            {latestVersion && course.draftChangedAt && <Badge>Unpublished changes</Badge>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {course.status === 'published' && (
            <>
              <CopyLinkButton path={`/c/${course.slug}`} />
              <Link to={`/c/${course.slug}`}>
                <Button variant="ghost">
                  <BookOpen className="size-4" aria-hidden /> View
                </Button>
              </Link>
            </>
          )}
          {course.status !== 'archived' ? (
            <Button disabled={publish.isPending || !changed} onClick={() => publish.mutate()}>
              {publish.isPending ? 'Publishing…' : latestVersion ? 'Publish changes' : 'Publish'}
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => archive.mutate(false)}>
              Restore
            </Button>
          )}
          {course.status === 'published' && (
            <Button
              variant="ghost"
              onClick={() =>
                window.confirm('Archive this course? Learners will no longer see it.') &&
                archive.mutate(true)
              }
            >
              Archive
            </Button>
          )}
        </div>
      </div>
      {latestVersion && course.draftChangedAt && (
        <p className="mt-2 text-sm text-muted-foreground">
          Learners see version {latestVersion.version}. Publish to give them your changes; people
          already taking the course can switch when they're ready.
        </p>
      )}
      <ErrorText error={publish.error ?? archive.error} />
    </div>
  );
}

const newModuleId = () => `m${Math.random().toString(36).slice(2, 8)}`;

function OutlineEditor({ draft }: { draft: CourseDraft }) {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { course, lessons } = draft;
  const byId = useMemo(() => new Map(lessons.map((l) => [l.id, l])), [lessons]);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['courseDraft', course.id] });
  const saveOutline = useMutation({
    mutationFn: (outline: CourseOutline) => api.updateOutline(course.id, outline),
    onSuccess: refresh,
  });
  const addLesson = useMutation({
    mutationFn: ({ moduleId, moduleTitle }: { moduleId: string; moduleTitle?: string }) =>
      api.createLesson(
        course.id,
        moduleId,
        { title: 'Untitled lesson', optional: false, blocks: [] },
        moduleTitle,
      ),
    onSuccess: (lesson) => navigate(`/courses/${course.id}/lessons/${lesson.id}/edit`),
  });
  const removeLesson = useMutation({
    mutationFn: (id: string) => api.deleteLesson(id),
    onSuccess: refresh,
  });
  // A customized course: its own title and note for the source's lessons.
  const changeSource = useMutation({
    mutationFn: (v: { id: string; title?: string | null; note?: string | null }) =>
      api.changeSourceLesson(course.id, v.id, { title: v.title, note: v.note }),
    onSuccess: refresh,
  });
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const modules = course.outline.modules;
  const update = (next: CourseOutline['modules']) => saveOutline.mutate({ modules: next });
  const aiEnabled = useAiEnabled();
  const [assist, setAssist] = useState(false);
  const move = <T,>(list: T[], i: number, d: number) => {
    const next = [...list];
    const [x] = next.splice(i, 1);
    next.splice(i + d, 0, x!);
    return next;
  };

  return (
    <div className="space-y-4">
      {modules.length === 0 && (
        <Card>
          <p className="text-sm text-muted-foreground">
            Courses are made of modules (like "Week 1" or "Getting started") that hold lessons. Add
            your first module to begin.
          </p>
        </Card>
      )}
      {modules.map((m, mi) => (
        <Card key={m.id}>
          <div className="mb-3 flex items-center gap-1">
            <Input
              aria-label="Module title"
              className="min-w-0 flex-1 font-medium"
              defaultValue={m.title}
              onBlur={(e) => {
                const title = e.target.value.trim();
                if (title && title !== m.title)
                  update(modules.map((x) => (x.id === m.id ? { ...x, title } : x)));
              }}
            />
            <IconAction
              label="Move module up"
              disabled={mi === 0}
              onClick={() => update(move(modules, mi, -1))}
            >
              <ArrowUp className="size-4" />
            </IconAction>
            <IconAction
              label="Move module down"
              disabled={mi === modules.length - 1}
              onClick={() => update(move(modules, mi, 1))}
            >
              <ArrowDown className="size-4" />
            </IconAction>
            <IconAction
              label="Delete module"
              disabled={m.lessonIds.length > 0}
              onClick={() => update(modules.filter((x) => x.id !== m.id))}
            >
              <Trash2 className="size-4" />
            </IconAction>
          </div>
          <ol className="space-y-2">
            {m.lessonIds.map((lid, li) => {
              const lesson = byId.get(lid);
              if (!lesson) return null;
              return (
                <li
                  key={lid}
                  className="flex flex-col gap-1 rounded-lg border border-border p-2 sm:flex-row sm:items-center"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <span className="w-6 shrink-0 text-center text-sm tabular-nums text-muted-foreground">
                      {li + 1}
                    </span>
                    {lesson.fromSource ? (
                      <div className="min-w-0 flex-1">
                        <Input
                          aria-label="Lesson title"
                          className="font-medium"
                          defaultValue={lesson.title}
                          onBlur={(e) => {
                            const title = e.target.value.trim();
                            if (title && title !== lesson.title)
                              changeSource.mutate({
                                id: lid,
                                title: title === lesson.sourceTitle ? null : title,
                              });
                          }}
                        />
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          From the source
                          {lesson.title !== lesson.sourceTitle && ` · was “${lesson.sourceTitle}”`}
                          {lesson.optional && ' · Optional'} ·{' '}
                          <button
                            type="button"
                            className="-my-2 py-2 text-primary hover:underline"
                            onClick={() => setNoteFor(noteFor === lid ? null : lid)}
                          >
                            {lesson.note ? 'Edit your note' : 'Add a note'}
                          </button>
                        </span>
                        {noteFor === lid && (
                          <Textarea
                            aria-label="Note shown at the top of this lesson"
                            className="mt-2"
                            rows={3}
                            defaultValue={lesson.note ?? ''}
                            placeholder="Shown at the top of the lesson, e.g. how this applies at your organization. Markdown works."
                            onBlur={(e) => {
                              const note = e.target.value.trim();
                              if (note !== (lesson.note ?? ''))
                                changeSource.mutate({ id: lid, note: note || null });
                            }}
                          />
                        )}
                      </div>
                    ) : (
                      <Link
                        to={`/courses/${course.id}/lessons/${lid}/edit`}
                        className="min-w-0 flex-1 hover:text-primary"
                      >
                        <span className="block truncate font-medium">{lesson.title}</span>
                        <span className="block text-xs text-muted-foreground">
                          {lesson.blockCount === 0
                            ? 'Empty'
                            : `${lesson.blockCount} block${lesson.blockCount === 1 ? '' : 's'}`}
                          {lesson.optional && ' · Optional'}
                          {draft.overlay && ' · Yours'}
                        </span>
                      </Link>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center justify-end">
                    {!lesson.fromSource && (
                      <Link
                        to={`/courses/${course.id}/lessons/${lid}/edit?preview=1`}
                        aria-label={`Preview ${lesson.title}`}
                        title="Preview"
                        className="flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                      >
                        <Eye className="size-4" />
                      </Link>
                    )}
                    {modules.length > 1 && (
                      <Select
                        aria-label="Move to module"
                        className="w-auto"
                        value=""
                        onChange={(e) => {
                          const to = e.target.value;
                          if (!to) return;
                          update(
                            modules.map((x) =>
                              x.id === m.id
                                ? { ...x, lessonIds: x.lessonIds.filter((id) => id !== lid) }
                                : x.id === to
                                  ? { ...x, lessonIds: [...x.lessonIds, lid] }
                                  : x,
                            ),
                          );
                        }}
                      >
                        <option value="">Move to…</option>
                        {modules
                          .filter((x) => x.id !== m.id)
                          .map((x) => (
                            <option key={x.id} value={x.id}>
                              {x.title}
                            </option>
                          ))}
                      </Select>
                    )}
                    <IconAction
                      label="Move lesson up"
                      disabled={li === 0}
                      onClick={() =>
                        update(
                          modules.map((x) =>
                            x.id === m.id ? { ...x, lessonIds: move(x.lessonIds, li, -1) } : x,
                          ),
                        )
                      }
                    >
                      <ArrowUp className="size-4" />
                    </IconAction>
                    <IconAction
                      label="Move lesson down"
                      disabled={li === m.lessonIds.length - 1}
                      onClick={() =>
                        update(
                          modules.map((x) =>
                            x.id === m.id ? { ...x, lessonIds: move(x.lessonIds, li, 1) } : x,
                          ),
                        )
                      }
                    >
                      <ArrowDown className="size-4" />
                    </IconAction>
                    {lesson.fromSource ? (
                      <IconAction
                        label="Leave this lesson out"
                        onClick={() =>
                          update(
                            modules.map((x) =>
                              x.id === m.id
                                ? { ...x, lessonIds: x.lessonIds.filter((id) => id !== lid) }
                                : x,
                            ),
                          )
                        }
                      >
                        <EyeOff className="size-4" />
                      </IconAction>
                    ) : (
                      <IconAction
                        label="Delete lesson"
                        onClick={() =>
                          window.confirm(`Delete "${lesson.title}"? This can't be undone.`) &&
                          removeLesson.mutate(lid)
                        }
                      >
                        <Trash2 className="size-4" />
                      </IconAction>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
          <Button
            type="button"
            variant="ghost"
            className="mt-2"
            disabled={addLesson.isPending}
            onClick={() => addLesson.mutate({ moduleId: m.id })}
          >
            <Plus className="size-4" aria-hidden /> Add lesson
          </Button>
        </Card>
      ))}
      {draft.overlay &&
        (draft.overlay.hiddenLessons.length > 0 || draft.overlay.hiddenModules.length > 0) && (
          <Card title="Left out from the source">
            <p className="mb-2 text-sm text-muted-foreground">
              These stay out when the source publishes new versions. Put any back here.
            </p>
            <ul className="divide-y divide-border">
              {draft.overlay.hiddenModules.map((hm) => (
                <li key={hm.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span>
                    <span className="font-medium">{hm.title}</span>{' '}
                    <span className="text-muted-foreground">(module)</span>
                  </span>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      update([...modules, { id: hm.id, title: hm.title, lessonIds: [] }])
                    }
                  >
                    Put back
                  </Button>
                </li>
              ))}
              {draft.overlay.hiddenLessons.map((hl) => (
                <li key={hl.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span className="font-medium">{hl.title}</span>
                  <Button
                    variant="secondary"
                    disabled={!modules.length}
                    onClick={() => {
                      const home = modules.find((x) => x.id === hl.moduleId) ?? modules[0]!;
                      update(
                        modules.map((x) =>
                          x.id === home.id ? { ...x, lessonIds: [...x.lessonIds, hl.id] } : x,
                        ),
                      );
                    }}
                  >
                    Put back
                  </Button>
                </li>
              ))}
            </ul>
          </Card>
        )}
      {assist && (
        <OutlineAssist course={course} owner={ownerOf(course)} onClose={() => setAssist(false)} />
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="secondary"
          disabled={saveOutline.isPending}
          onClick={() =>
            update([
              ...modules,
              { id: newModuleId(), title: `Module ${modules.length + 1}`, lessonIds: [] },
            ])
          }
        >
          <Plus className="size-4" aria-hidden /> Add module
        </Button>
        {aiEnabled && !assist && (
          <AiButton type="button" onClick={() => setAssist(true)}>
            Draft {modules.length ? 'more modules' : 'an outline'} with AI
          </AiButton>
        )}
      </div>
      <ErrorText
        error={saveOutline.error ?? addLesson.error ?? removeLesson.error ?? changeSource.error}
      />
    </div>
  );
}

function IconAction({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-30"
    >
      {children}
    </button>
  );
}

function CourseDetails({ course }: { course: Course }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const owner = ownerOf(course);
  const [form, setForm] = useState<CourseInput>(() => ({
    title: course.title,
    summary: course.summary ?? undefined,
    description: course.description ?? undefined,
    outcomes: course.outcomes,
    level: course.level,
    estimatedMinutes: course.estimatedMinutes ?? undefined,
    tags: course.tags,
    topics: course.topics,
    coverAssetId: course.coverAssetId,
    audience: course.audience,
    audienceScope: course.audienceScope ?? null,
    reuse: course.reuse ?? null,
    settings: course.settings,
  }));
  const quizzes = useQuery({
    queryKey: ['quizzes', ownerKey(owner), 'course'],
    queryFn: () => api.quizzes(owner, { audience: 'course' }),
  });
  // Only course assessments with the same owner (each belongs to one course).
  const finalQuizzes = (quizzes.data?.quizzes ?? []).filter(
    (q) => q.audience === 'course' && q.status !== 'archived',
  );
  const navigate = useNavigate();
  const createAssessment = useMutation({
    mutationFn: async () => {
      const quiz = await api.createQuiz(owner, {
        title: `${form.title}: final assessment`,
        mode: 'assessment',
        audience: 'course',
        settings: {
          certificate: false,
          shuffleQuestions: true,
          passPercent: 70,
          maxAttempts: null,
        },
      });
      await api.updateCourse(course.id, {
        ...form,
        settings: { ...form.settings, finalQuizId: quiz.id },
      });
      return quiz;
    },
    onSuccess: (quiz) => navigate(`/quizzes/${quiz.id}/edit`),
  });
  const save = useMutation({
    mutationFn: () => api.updateCourse(course.id, form),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['courseDraft', course.id] }),
  });
  const set = (patch: Partial<CourseInput>) => setForm((f) => ({ ...f, ...patch }));
  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Card title="About the course">
        <div className="space-y-4">
          <Field label="Title" htmlFor="c-title">
            <Input
              id="c-title"
              required
              minLength={3}
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
            />
          </Field>
          <Field label="One-line summary" htmlFor="c-summary">
            <Input
              id="c-summary"
              maxLength={500}
              value={form.summary ?? ''}
              onChange={(e) => set({ summary: e.target.value || undefined })}
            />
          </Field>
          <Field label="Description (Markdown)" htmlFor="c-desc">
            <Textarea
              id="c-desc"
              rows={6}
              className="font-mono text-sm"
              value={form.description ?? ''}
              onChange={(e) => set({ description: e.target.value || undefined })}
            />
          </Field>
          <Field
            label="What learners will be able to do"
            htmlFor="c-outcomes"
            hint="One outcome per line."
          >
            <Textarea
              id="c-outcomes"
              rows={4}
              value={form.outcomes.join('\n')}
              onChange={(e) =>
                set({
                  outcomes: e.target.value
                    .split('\n')
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .slice(0, 20),
                })
              }
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Level" htmlFor="c-level">
              <Select
                id="c-level"
                value={form.level}
                onChange={(e) => set({ level: e.target.value as CourseInput['level'] })}
              >
                {COURSE_LEVELS.map((l) => (
                  <option key={l} value={l}>
                    {l.charAt(0).toUpperCase() + l.slice(1)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Time to complete (minutes)" htmlFor="c-minutes">
              <Input
                id="c-minutes"
                type="number"
                min={1}
                value={form.estimatedMinutes ?? ''}
                onChange={(e) =>
                  set({ estimatedMinutes: e.target.value ? Number(e.target.value) : undefined })
                }
              />
            </Field>
            <Field label="Search tags" htmlFor="c-tags" hint="Comma-separated.">
              <Input
                id="c-tags"
                value={form.tags.join(', ')}
                onChange={(e) =>
                  set({
                    tags: e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean)
                      .slice(0, 20),
                  })
                }
              />
            </Field>
          </div>
          <TopicPicker value={form.topics} onChange={(topics) => set({ topics })} />
          <Field label="Cover image (optional)" htmlFor="c-cover">
            <FilePicker
              owner={owner}
              courseId={course.id}
              kinds={['image']}
              label="cover image"
              value={form.coverAssetId ?? null}
              onChange={(id) => set({ coverAssetId: id })}
            />
          </Field>
        </div>
      </Card>
      <Card title="Access and completion">
        <div className="space-y-4">
          <AudiencePicker
            owner={owner}
            id="c-audience"
            label="Who can take it"
            audience={form.audience}
            scope={form.audienceScope ?? null}
            onChange={(a, s) => set({ audience: a as Audience, audienceScope: s })}
          />
          <ReuseSettings
            kind="course"
            owner={owner}
            value={form.reuse}
            onChange={(reuse) => set({ reuse })}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-5 accent-primary"
              checked={form.settings.certificate}
              onChange={(e) =>
                set({ settings: { ...form.settings, certificate: e.target.checked } })
              }
            />
            Award a certificate when learners complete the course
          </label>
          <Field
            label="Final assessment (optional)"
            htmlFor="c-quiz"
            hint="Opens to learners once they finish the required lessons; passing it completes the course. It's never listed or shared anywhere else. Publish it before the course."
          >
            <Select
              id="c-quiz"
              value={form.settings.finalQuizId ?? ''}
              onChange={(e) =>
                set({ settings: { ...form.settings, finalQuizId: e.target.value || null } })
              }
            >
              <option value="">None: completing the lessons is enough</option>
              {finalQuizzes.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.title}
                  {q.status === 'draft' ? ' (draft)' : ''}
                </option>
              ))}
            </Select>
          </Field>
          <div className="flex flex-wrap gap-2">
            {form.settings.finalQuizId ? (
              <>
                <Link to={`/quizzes/${form.settings.finalQuizId}/edit`}>
                  <Button type="button" variant="secondary">
                    Edit the assessment
                  </Button>
                </Link>
                <Link to={`/c/${course.slug}/assessment?preview=1`}>
                  <Button type="button" variant="ghost">
                    <Eye className="size-4" aria-hidden /> Preview
                  </Button>
                </Link>
              </>
            ) : (
              <Button
                type="button"
                variant="secondary"
                disabled={createAssessment.isPending}
                onClick={() => createAssessment.mutate()}
              >
                <Plus className="size-4" aria-hidden /> Create the final assessment
              </Button>
            )}
          </div>
          <ErrorText error={createAssessment.error} />
        </div>
      </Card>
      <div className="flex items-center gap-3">
        <Button type="submit" className="w-full sm:w-auto" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save details'}
        </Button>
        {save.isSuccess && !save.isPending && (
          <span className="text-sm text-muted-foreground">Saved</span>
        )}
      </div>
      <ErrorText error={save.error} />
    </form>
  );
}

function CourseFiles({ course }: { course: Course }) {
  const api = useApi();
  const owner = ownerOf(course);
  const files = useCourseFiles(owner);
  const queryClient = useQueryClient();
  const { upload, state } = useUpload(owner, course.id);
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteCourseFile(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['courseFiles'] }),
  });
  const list = (files.data?.files ?? []).filter((f) => f.courseId === course.id || !f.courseId);
  return (
    <div className="space-y-4">
      <Card title="Upload files">
        <p className="mb-3 text-sm text-muted-foreground">
          Videos (up to 500 MB), audio, slides, PDFs, Word and Excel files, notebooks, code and
          images. Every file is checked for viruses before learners can see it.
        </p>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--radius)] border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-muted">
          <Plus className="size-4" aria-hidden /> Choose files
          <input
            type="file"
            multiple
            className="sr-only"
            onChange={async (e) => {
              const picked = [...(e.target.files ?? [])];
              e.target.value = '';
              for (const f of picked) await upload(f);
            }}
          />
        </label>
        {state && state.phase !== 'done' && (
          <div className="mt-3">
            <UploadProgress state={state} />
          </div>
        )}
      </Card>
      <Card title={`Files (${list.length})`}>
        {list.length === 0 ? (
          <p className="text-sm text-muted-foreground">No files yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {list.map((f) => (
              <li
                key={f.id}
                className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:gap-3"
              >
                <div className="min-w-0 flex-1">
                  {f.url ? (
                    <a
                      href={f.url}
                      download={f.filename}
                      className="block truncate font-medium hover:text-primary"
                    >
                      {f.filename}
                    </a>
                  ) : (
                    <span className="block truncate font-medium">{f.filename}</span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {formatBytes(f.sizeBytes)} · {STATUS_LABEL[f.status]}
                    {f.previewStatus === 'pending' && ' · Preparing preview…'}
                    {f.previewStatus === 'ready' && ' · Preview ready'}
                  </span>
                </div>
                <IconAction
                  label={`Delete ${f.filename}`}
                  onClick={() => window.confirm(`Delete ${f.filename}?`) && remove.mutate(f.id)}
                >
                  <Trash2 className="size-4" />
                </IconAction>
              </li>
            ))}
          </ul>
        )}
        <ErrorText error={remove.error} />
      </Card>
    </div>
  );
}

// ─── Lesson editor ─────────────────────────────────────────────────────

export function LessonEditorPage() {
  const { courseId = '', lessonId = '' } = useParams();
  const api = useApi();
  const draft = useQuery({
    queryKey: ['courseDraft', courseId],
    queryFn: () => api.courseDraft(courseId),
  });
  const lesson = useQuery({
    queryKey: ['lessonDraft', lessonId],
    queryFn: () => api.lessonDraft(lessonId),
  });
  if (draft.isPending || lesson.isPending)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (draft.isError || lesson.isError) return <ErrorText error={draft.error ?? lesson.error} />;
  return <LessonEditor key={lesson.data.id} course={draft.data.course} lesson={lesson.data} />;
}

function LessonEditor({ course, lesson }: { course: Course; lesson: DraftLesson }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const owner = ownerOf(course);
  const [title, setTitle] = useState(lesson.title);
  const [summary, setSummary] = useState(lesson.summary ?? '');
  const [optional, setOptional] = useState(lesson.optional);
  const [minutes, setMinutes] = useState(lesson.estimatedMinutes ?? undefined);
  const [blocks, setBlocks] = useState<DraftBlock[]>(lesson.blocks);
  const [dirty, setDirty] = useState(false);
  const [picking, setPicking] = useState(lesson.blocks.length === 0);
  const aiEnabled = useAiEnabled();
  const [assist, setAssist] = useState(false);
  const [params, setParams] = useSearchParams();
  const previewing = params.get('preview') === '1';
  const setPreviewing = (on: boolean) => setParams(on ? { preview: '1' } : {}, { replace: true });
  const pendingCount = blocks.filter(isPending).length;

  // Warn before leaving with unsaved changes.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const input = (): LessonInput => ({
    title,
    summary: summary || undefined,
    optional,
    estimatedMinutes: minutes,
    blocks: blocks.filter((b): b is Block => !isPending(b)),
  });
  const save = useMutation({
    mutationFn: () => api.updateLesson(lesson.id, input()),
    onSuccess: () => {
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ['courseDraft', course.id] });
    },
  });
  const change = (fn: (b: DraftBlock[]) => DraftBlock[]) => {
    setBlocks(fn);
    setDirty(true);
  };
  const ordered = course.outline.modules.flatMap((m) => m.lessonIds);
  const i = ordered.indexOf(lesson.id);

  return (
    <div className="space-y-6 pb-24">
      <div>
        <Link
          to={`/courses/${course.id}/edit`}
          className="-my-2 inline-block py-2 text-sm text-muted-foreground hover:text-foreground"
        >
          ← {course.title}
        </Link>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold">
            {previewing ? 'Preview lesson' : 'Edit lesson'}
          </h1>
          <div className="flex rounded-[var(--radius)] border border-border p-0.5" role="group">
            {(['Edit', 'Preview'] as const).map((label) => {
              const active = (label === 'Preview') === previewing;
              return (
                <button
                  key={label}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setPreviewing(label === 'Preview')}
                  className={`min-h-9 rounded-[calc(var(--radius)-2px)] px-4 text-sm font-medium ${active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {previewing ? (
        <LessonPreview lessonId={lesson.id} input={input()} />
      ) : (
        <>
          <Card>
            <div className="space-y-4">
              <Field label="Lesson title" htmlFor="l-title">
                <Input
                  id="l-title"
                  required
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setDirty(true);
                  }}
                />
              </Field>
              <Field label="Summary (optional)" htmlFor="l-summary">
                <Input
                  id="l-summary"
                  value={summary}
                  onChange={(e) => {
                    setSummary(e.target.value);
                    setDirty(true);
                  }}
                />
              </Field>
              <div className="flex flex-wrap items-end gap-4">
                <Field label="Minutes (optional)" htmlFor="l-min">
                  <Input
                    id="l-min"
                    type="number"
                    min={1}
                    className="w-28"
                    value={minutes ?? ''}
                    onChange={(e) => {
                      setMinutes(e.target.value ? Number(e.target.value) : undefined);
                      setDirty(true);
                    }}
                  />
                </Field>
                <label className="flex items-center gap-2 pb-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-5 accent-primary"
                    checked={optional}
                    onChange={(e) => {
                      setOptional(e.target.checked);
                      setDirty(true);
                    }}
                  />
                  Optional (doesn't count toward completion)
                </label>
              </div>
            </div>
          </Card>

          {blocks.map((b, bi) => (
            <BlockCard
              key={b.id}
              block={b}
              index={bi}
              count={blocks.length}
              owner={owner}
              courseId={course.id}
              lessonId={lesson.id}
              onChange={(nb) => change((all) => all.map((x) => (x.id === b.id ? nb : x)))}
              onRemove={() => change((all) => all.filter((x) => x.id !== b.id))}
              onMove={(d) =>
                change((all) => {
                  const next = [...all];
                  const [x] = next.splice(bi, 1);
                  next.splice(bi + d, 0, x!);
                  return next;
                })
              }
            />
          ))}

          {assist && (
            <LessonAssist
              lessonId={lesson.id}
              owner={owner}
              dirty={dirty}
              hasText={blocks.some((b) => b.type === 'markdown' || b.type === 'callout')}
              onClose={() => setAssist(false)}
              onInsert={(d) => {
                change((all) => [...all, ...d.blocks.map((b) => ({ ...b, id: newBlockId() }))]);
                if (!summary) setSummary(d.summary);
                if (!minutes) setMinutes(d.estimatedMinutes);
                setPicking(false);
              }}
            />
          )}

          {picking ? (
            <Card title="Add a block">
              <div className="grid gap-2 sm:grid-cols-2">
                {BLOCK_TYPES.map((t) => (
                  <button
                    key={t.type}
                    type="button"
                    onClick={() => {
                      const created = t.create();
                      change((all) => [
                        ...all,
                        created ?? { id: newBlockId(), type: t.type, pending: true },
                      ]);
                      setPicking(false);
                    }}
                    className="flex items-start gap-3 rounded-lg border border-border p-3 text-left hover:bg-muted"
                  >
                    <span className="text-primary" aria-hidden>
                      {t.icon}
                    </span>
                    <span>
                      <span className="block text-sm font-medium">{t.label}</span>
                      <span className="block text-xs text-muted-foreground">{t.hint}</span>
                    </span>
                  </button>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {aiEnabled && !assist && (
                  <AiButton
                    type="button"
                    onClick={() => {
                      setAssist(true);
                      setPicking(false);
                    }}
                  >
                    Write with AI
                  </AiButton>
                )}
                {blocks.length > 0 && (
                  <Button type="button" variant="ghost" onClick={() => setPicking(false)}>
                    Cancel
                  </Button>
                )}
              </div>
            </Card>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => setPicking(true)}
              >
                <Plus className="size-4" aria-hidden /> Add a block
              </Button>
              {aiEnabled && !assist && (
                <AiButton
                  type="button"
                  className="w-full sm:w-auto"
                  onClick={() => setAssist(true)}
                >
                  Write with AI
                </AiButton>
              )}
            </div>
          )}
        </>
      )}

      {/* Sticky save bar: always reachable on phones. */}
      <div className="fixed inset-x-0 bottom-0 z-10 border-t border-border bg-background/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3">
          <Button
            className="flex-1 sm:flex-none"
            disabled={!dirty || save.isPending || !title.trim()}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : dirty ? 'Save lesson' : 'Saved'}
          </Button>
          {pendingCount > 0 && (
            <span className="text-xs text-muted-foreground">
              {pendingCount} block{pendingCount === 1 ? ' needs a file' : 's need files'} and won't
              be saved yet.
            </span>
          )}
          <div className="ml-auto flex gap-2">
            {i > 0 && (
              <Link to={`/courses/${course.id}/lessons/${ordered[i - 1]}/edit`}>
                <Button variant="ghost">Previous</Button>
              </Link>
            )}
            {i >= 0 && i < ordered.length - 1 && (
              <Link to={`/courses/${course.id}/lessons/${ordered[i + 1]}/edit`}>
                <Button variant="ghost">Next</Button>
              </Link>
            )}
          </div>
          <div className="w-full">
            <ErrorText error={save.error} />
          </div>
        </div>
      </div>
    </div>
  );
}
